import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, BookOpen, FileText, RefreshCw, BarChart3, Plug, Zap } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import { setRefCodeCookie } from "@/lib/affiliateConfig";
import RankaroLogo from "@/components/shared/RankaroLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [urgencyVisible, setUrgencyVisible] = useState(true);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Track scroll for header background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track affiliate referral code
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setRefCodeCookie(refCode);
    }
  }, [searchParams]);

  const navLinks = [
    { label: t('header.features'), href: "/#features" },
    { label: t('header.howItWorks'), href: "/#how-it-works" },
    { label: t('header.pricing'), href: "/#pricing" },
    { label: t('header.faq'), href: "/#faq" },
  ];

  const seoPages = [
    { label: "SEO WooCommerce", href: "/seo-woocommerce", icon: BookOpen, desc: "Guía completa" },
    { label: "Descripciones SEO", href: "/descripciones-productos-seo", icon: FileText, desc: "Cómo escribir" },
    { label: "Alternativa Yoast", href: "/alternativa-yoast", icon: RefreshCw, desc: "Compara opciones" },
    { label: "RankMath vs Rankaro", href: "/rank-math-vs-rankaro", icon: BarChart3, desc: "Comparativa" },
    { label: "Plugin SEO", href: "/plugin-seo-woocommerce", icon: Plug, desc: "El mejor 2026" },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "glass-effect shadow-sm" 
          : "bg-background/80 backdrop-blur-sm md:bg-transparent"
      }`}
    >
      {urgencyVisible && (
        <div className="relative bg-gradient-to-r from-primary to-accent text-white text-center py-2 px-3 md:py-2.5 md:px-4">
          <div className="container mx-auto flex items-center justify-center gap-3">
            <Zap size={16} className="flex-shrink-0 fill-current" />
            <Link to="/auth?mode=signup" className="text-xs sm:text-sm font-medium hover:underline">
              {t('urgencyBar.text')}
            </Link>
            <button
              onClick={() => setUrgencyVisible(false)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <RankaroLogo variant="full" size={44} className="transition-transform group-hover:scale-105" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors link-animated"
              >
                {link.label}
              </a>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger className="relative px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                {t('header.guides') || 'Guías SEO'}
                <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-64 bg-popover border border-border shadow-lg">
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
                  Recursos SEO
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {seoPages.map((page) => (
                  <DropdownMenuItem key={page.href} asChild>
                    <Link to={page.href} className="flex items-center gap-3 py-2 cursor-pointer">
                      <page.icon className="h-4 w-4 text-primary" />
                      <div className="flex flex-col">
                        <span className="font-medium">{page.label}</span>
                        <span className="text-xs text-muted-foreground">{page.desc}</span>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                {t('header.login')}
              </Button>
            </Link>
            <Link to="/auth?mode=signup">
              <Button variant="hero" size="sm">
                {t('header.startFree')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground rounded-lg hover:bg-muted/50 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-fade-in">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {t('header.guides') || 'Guías SEO'}
                </p>
                {seoPages.map((page) => (
                  <Link
                    key={page.href}
                    to={page.href}
                    className="block px-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {page.label}
                  </Link>
                ))}
              </div>
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/50 px-4">
                <div className="flex justify-center mb-3">
                  <LanguageSwitcher />
                </div>
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full">
                    {t('header.login')}
                  </Button>
                </Link>
                <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="hero" size="sm" className="w-full">
                    {t('header.startFree')}
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
